import React, { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CreateNewMember from "components/Teams/createNewMember/CreateNewMember";
import PaymentInfo from "components/branchDetails/PaymentInfo";
import Header from "components/common/header/Header";
import { AuthProvider } from "hooks/authentication/useAuth";
import { BranchNameProvider } from "hooks/branchName/useBranchName";
import { PermissionsProvider } from "hooks/permission/usePermission";
import { RequireAuth } from "middleware/RequireAuth";

import ImprintPage from "pages/Imprint";
import AGBPage from "pages/agb";
import BranchUserDetail from "pages/branches/branchUserDetail/BranchUserDetail";
import EditBranch from "pages/branches/editBranch/EditBranch";
import CreatePassword from "pages/createPassword/CreatePassword";
import DigitalSolutionPage from "pages/digitalSolution";
import DigitalSolutionBPage from "pages/digitalSolutionB";
import Empty from "pages/empty/Empty";
import ForgetPassword from "pages/forgetPassword/ForgetPassword";
import FutureCarePage from "pages/futurecare";
import GeccoPage from "pages/gecco";
import Login from "pages/login/Login";
import NotFound from "pages/notFound.js/NotFound";
import PasswordCreated from "pages/passwordCreated/PasswordCreated";
import PrivacyPolicyPage from "pages/privacyPolicy";
import Profile from "pages/profile/Profile";
import UnauthorizedUser from "pages/unauthorizedUser/UnauthorizedUser";
import ValidateUser from "pages/validateUser/ValidateUser";

import EditUser from "./pages/userManagement/editUser/EditUser";

import "common/common.scss";
import "scss/app.scss";
import { UserProfilePictureProvider } from "hooks/UserProfilePicture/UserProfilePicture";
import UploadBranchDocument from "components/branchDetails/UploadBranchDocument";
import TeamsPage from "pages/TeamsPage/TeamsPage";
import TeamMemberDetail from "components/Teams/teamMemberDetail/TeamMemberDetail";
import DownloadData from "pages/downloadData/DownloadData";

const App = () => {
  const queryClient = new QueryClient();
  const [reloaded, setReloaded] = useState(false);
  useEffect(() => {
    document.addEventListener("localdatachanged", () => {
      if (!reloaded) setReloaded(true);
    });
  }, []);

  const theme = createTheme({
    palette: {
      primary: {
        main: "#0D61AF",
      },
      common: {
        black: "#0D61AF",
      },
      color: {
        blueLight: "#316FC8",
        blueDark: "#0D61AF",
        blackV1: "#0D0914",
        primary: "#6200ee",
      },
      background: {
        paper: "#ffffff",
      },
    },
  });
  return (
    <AuthProvider>
      <PermissionsProvider>
        <BranchNameProvider>
          <UserProfilePictureProvider>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider theme={theme}>
                <Routes>
                  {/* <Route path="/" exact element={<Navigate to="/home" replace />} /> */}
                  <Route path="/" exact element={<Empty />} />
                  <Route path="/home" element={<Empty />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/validate-link/:id" element={<ValidateUser />} />
                  <Route path="/forget-password" element={<ForgetPassword />} />
                  <Route
                    path="/download-data/:bankId/:userId/:token"
                    element={<DownloadData />}
                  />
                  <Route
                    path="/password-created"
                    element={<PasswordCreated />}
                  />
                  <Route path="/create-password" element={<CreatePassword />} />

                  {/* landing page routes start */}
                  <Route path="/gecco" element={<GeccoPage />} />
                  <Route path="/futurecare" element={<FutureCarePage />} />
                  <Route
                    path="/digitalsolution"
                    element={<DigitalSolutionPage />}
                  />
                  <Route path="/uber-uns" element={<DigitalSolutionBPage />} />
                  <Route path="/imprint" element={<ImprintPage />} />
                  <Route
                    path="/datenschutzhinweise"
                    element={<PrivacyPolicyPage />}
                  />
                  <Route path="/AGB" element={<AGBPage />} />
                  {/* landing page routes end */}

                  <Route
                    path="/unauthorized-user"
                    element={
                      <RequireAuth>
                        <UnauthorizedUser />
                      </RequireAuth>
                    }
                  />

                  {localStorage.getItem("menuStructure") != "undefined" &&
                    JSON.parse(localStorage.getItem("menuStructure"))
                      ?.filter(
                        (item) =>
                          !item?.menuStructureId?.includes("FutureCare") &&
                          item.componentName != "Products"
                      )
                      ?.map((item) => {
                        let Component = require("pages" +
                          item?.componentPath)?.default;
                        return (
                          <Route
                            key={item.route}
                            path={item.route}
                            element={
                              <RequireAuth>
                                <Component />
                              </RequireAuth>
                            }
                          />
                        );
                      })}
                  <Route
                    path="/branch-detail/teams"
                    element={
                      <RequireAuth>
                        <TeamsPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/branch-detail/teams/:teamMemberId"
                    element={
                      <RequireAuth>
                        <TeamMemberDetail />
                      </RequireAuth>
                    }
                  />
                  <Route path="/branch-detail/teams/createNewMember">
                    <Route
                      path=":teamMemberId"
                      element={
                        <RequireAuth>
                          <CreateNewMember />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path=""
                      element={
                        <RequireAuth>
                          <CreateNewMember />
                        </RequireAuth>
                      }
                    />
                  </Route>
                  <Route
                    path="/branch-detail/payment-info"
                    element={
                      <RequireAuth>
                        <PaymentInfo />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/branch-detail/:productId/upload-document"
                    element={
                      <RequireAuth>
                        <UploadBranchDocument />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/branches/:branchId/users/:userId"
                    element={
                      <RequireAuth>
                        <BranchUserDetail />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/branches/edit-branch/:id"
                    element={
                      <RequireAuth>
                        <EditBranch />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/user-management/edit-user/:id"
                    element={
                      <RequireAuth>
                        <EditUser />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <RequireAuth>
                        <Profile />
                      </RequireAuth>
                    }
                  />
                </Routes>
              </ThemeProvider>

              <ReactQueryDevtools
                initialIsOpen={false}
                position={"bottom-right"}
              />
            </QueryClientProvider>
          </UserProfilePictureProvider>
        </BranchNameProvider>
      </PermissionsProvider>
    </AuthProvider>
  );
};

export default App;
